import React, { useState } from 'react';
import { Box, Flex, Select, Text } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';

import {
  IMcpAuditoriaFiltros,
  IMcpAuditoriaItem,
  IMcpFerramenta,
  McpResultado,
  useMcp,
} from '../../../hooks/useMcp';
import { IDataNoPagination } from '../../../types/shared';
import {
  diaDe,
  horaDe,
  jsonLegivel,
  resumirAlvo,
  ROTULO_RESULTADO,
  TOM_RESULTADO,
} from '../mcpUtils';
import {
  Aviso,
  BlocoMono,
  Bolinha,
  BotaoSecundario,
  Cartao,
  Etiqueta,
  Eyebrow,
  Interruptor,
  MONO,
  Pilula,
  TEXTO,
  TEXTO_SUAVE,
} from '../ui';

const POLLING_MS = 10000;

const RESULTADOS: { valor: '' | McpResultado; rotulo: string }[] = [
  { valor: '', rotulo: 'Todos' },
  { valor: 'ok', rotulo: 'OK' },
  { valor: 'negado', rotulo: 'Negado' },
  { valor: 'erro', rotulo: 'Erro' },
];

function Detalhe({ titulo, valor }: { titulo: string; valor: unknown }) {
  const texto = jsonLegivel(valor);
  if (!texto) return null;
  return (
    <Flex direction="column" gap="4px">
      <Eyebrow>{titulo}</Eyebrow>
      <BlocoMono maxH="220px">{texto}</BlocoMono>
    </Flex>
  );
}

function LinhaAtividade({ item }: { item: IMcpAuditoriaItem }) {
  const [aberta, setAberta] = useState(false);
  const alvo = resumirAlvo(item.alvo_tipo, item.alvo_id);
  return (
    <Box borderTop="1px solid rgba(15,23,42,.05)">
      <Flex
        as="button"
        type="button"
        w="100%"
        textAlign="left"
        align="center"
        gap="12px"
        p="10px 20px"
        wrap="wrap"
        aria-expanded={aberta}
        onClick={() => setAberta(v => !v)}
        _hover={{ bg: 'rgba(255,255,255,.7)' }}
      >
        <Text fontFamily={MONO} fontSize="12px" color={TEXTO_SUAVE} w="48px" flex="none">
          {horaDe(item.dt_criacao)}
        </Text>
        <Bolinha texto={item.usuario_nome ?? 'MCP'} tom={item.usuario_nome ? 'info' : 'neutro'} />
        <Box flex="1 1 220px" minW={0}>
          <Text
            fontFamily={MONO}
            fontSize="12.5px"
            fontWeight={600}
            color={TEXTO}
            wordBreak="break-all"
          >
            {item.ferramenta}
          </Text>
          <Text fontSize="12px" color={TEXTO_SUAVE} noOfLines={1}>
            {item.usuario_nome ?? 'só a chave'}
            {alvo && alvo !== '-' ? ` · ${alvo}` : ''}
            {item.mensagem ? ` · ${item.mensagem}` : ''}
          </Text>
        </Box>
        <Text fontFamily={MONO} fontSize="11.5px" color={TEXTO_SUAVE}>
          {item.endereco_ip ?? '-'}
        </Text>
        <Etiqueta tom={TOM_RESULTADO[item.resultado] ?? 'neutro'}>
          {ROTULO_RESULTADO[item.resultado] ?? item.resultado}
        </Etiqueta>
      </Flex>
      {aberta && (
        <Flex direction="column" gap="10px" p="4px 20px 16px 80px">
          {item.mensagem && (
            <Text fontSize="12.5px" color="#334155">
              {item.mensagem}
            </Text>
          )}
          <Detalhe titulo="PARÂMETROS" valor={item.parametros} />
          <Detalhe titulo="ANTES" valor={item.antes} />
          <Detalhe titulo="DEPOIS" valor={item.depois} />
          <Text fontFamily={MONO} fontSize="11px" color={TEXTO_SUAVE}>
            IP {item.endereco_ip ?? '-'}
            {item.confirmacao_id ? ` · confirmação ${item.confirmacao_id}…` : ''}
          </Text>
        </Flex>
      )}
    </Box>
  );
}

export function AtividadeTab({
  ferramentas,
  auditoriaDisponivel,
}: {
  ferramentas: IMcpFerramenta[];
  auditoriaDisponivel: boolean;
}) {
  const { getAuditoria } = useMcp();
  const [ferramenta, setFerramenta] = useState('');
  const [resultado, setResultado] = useState<'' | McpResultado>('');
  const [limite, setLimite] = useState(50);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const filtros: IMcpAuditoriaFiltros = { limite };
  if (ferramenta) filtros.ferramenta = ferramenta;
  if (resultado) filtros.resultado = resultado;

  const { data, isFetching, isError, refetch } = useQuery<IDataNoPagination<IMcpAuditoriaItem[]>>({
    queryKey: ['mcp-auditoria', filtros],
    queryFn: getAuditoria,
    refetchOnWindowFocus: false,
    refetchInterval: autoRefresh ? POLLING_MS : false,
    enabled: auditoriaDisponivel,
  });

  if (!auditoriaDisponivel) {
    return (
      <Cartao p="18px 20px">
        <Aviso tom="aviso">
          A tabela de auditoria do MCP não existe neste banco (a migration 0310 ainda não rodou),
          então não há atividade para mostrar.
        </Aviso>
      </Cartao>
    );
  }

  const itens = data?.data ?? [];

  return (
    <Flex direction="column" gap="16px">
      <Cartao p="18px 20px" display="flex" flexDirection="column" gap="12px">
        <Flex align="center" justify="space-between" gap="12px" wrap="wrap">
          <Eyebrow>ATIVIDADE DO MCP</Eyebrow>
          <Flex align="center" gap="10px">
            <Text fontSize="12.5px" color="#475569">
              Atualizar sozinho (10 s)
            </Text>
            <Interruptor
              ligado={autoRefresh}
              onChange={() => setAutoRefresh(v => !v)}
              rotulo="Atualizar sozinho"
            />
            <BotaoSecundario isLoading={isFetching} onClick={() => refetch()}>
              Atualizar
            </BotaoSecundario>
          </Flex>
        </Flex>
        <Flex gap="10px" wrap="wrap" align="center">
          <Select
            h="40px"
            maxW="280px"
            borderRadius="10px"
            bg="rgba(255,255,255,.85)"
            borderColor="#CBD5E1"
            fontSize="14px"
            aria-label="Ferramenta"
            value={ferramenta}
            onChange={e => setFerramenta(e.target.value)}
          >
            <option value="">Todas as ferramentas</option>
            {ferramentas.map(f => (
              <option key={f.nome} value={f.nome}>
                {f.nome}
              </option>
            ))}
          </Select>
          <Select
            h="40px"
            maxW="140px"
            borderRadius="10px"
            bg="rgba(255,255,255,.85)"
            borderColor="#CBD5E1"
            fontSize="14px"
            aria-label="Quantidade"
            value={limite}
            onChange={e => setLimite(Number(e.target.value))}
          >
            {[25, 50, 100, 200].map(n => (
              <option key={n} value={n}>
                Últimas {n}
              </option>
            ))}
          </Select>
        </Flex>
        <Flex gap="6px" wrap="wrap">
          {RESULTADOS.map(r => (
            <Pilula
              key={r.valor}
              ativa={r.valor === resultado}
              onClick={() => setResultado(r.valor)}
            >
              {r.rotulo}
            </Pilula>
          ))}
        </Flex>
        {isError && <Aviso tom="perigo">Não foi possível carregar a atividade.</Aviso>}
      </Cartao>

      <Cartao p={0} overflow="hidden">
        {itens.length === 0 && !isFetching && (
          <Text fontSize="13px" color={TEXTO_SUAVE} textAlign="center" py="28px">
            Nenhuma chamada registrada com esse filtro.
          </Text>
        )}
        {itens.map((item, i) => {
          const dia = diaDe(item.dt_criacao);
          const novoDia = i === 0 || dia !== diaDe(itens[i - 1].dt_criacao);
          return (
            <React.Fragment key={item.id}>
              {novoDia && (
                <Box
                  position="sticky"
                  top={0}
                  zIndex={1}
                  p="8px 20px"
                  bg="rgba(241,245,249,.92)"
                  borderTop={i === 0 ? undefined : '1px solid rgba(15,23,42,.05)'}
                >
                  <Eyebrow>{dia.toUpperCase()}</Eyebrow>
                </Box>
              )}
              <LinhaAtividade item={item} />
            </React.Fragment>
          );
        })}
      </Cartao>
    </Flex>
  );
}
